// Path: sec-parser/client/src/pages/UploadPage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Card, Upload, Button, Alert, Typography, Steps,
  Tag, Space, Progress, Divider, Row, Col, Input, Tabs,
} from "antd";
import {
  InboxOutlined, CheckCircleOutlined, LoadingOutlined,
  FileTextOutlined, RocketOutlined, LinkOutlined, GlobalOutlined,
} from "@ant-design/icons";
import { uploadFiling, submitEdgarUrl, getFilingStatus } from "../services/api";
import { addFiling } from "../store";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

// How to get the EDGAR XML URL:
// 1. Go to https://www.sec.gov/cgi-bin/browse-edgar
// 2. Search company name or CIK → select filing type (10-K, 10-Q)
// 3. Click the filing date → click the "-index.htm" link
// 4. Paste that full URL here — we'll find the XML/XBRL document automatically

const EDGAR_EXAMPLES = [
  { label: "Apple 10-K 2023",     url: "https://www.sec.gov/Archives/edgar/data/320193/000032019324000006/0000320193-24-000006-index.htm" },
  { label: "Microsoft 10-K 2023", url: "https://www.sec.gov/Archives/edgar/data/789019/000095017023035122/0000950170-23-035122-index.htm" },
  { label: "Tesla 10-K 2023",     url: "https://www.sec.gov/Archives/edgar/data/1318605/000131860524000010/0001318605-24-000010-index.htm" },
];

export default function UploadPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [state, setState]       = useState("idle"); // idle | loading | extracting | ready | error
  const [progress, setProgress] = useState(0);
  const [filingId, setFilingId] = useState(null);
  const [filingInfo, setFilingInfo] = useState(null);
  const [error, setError]       = useState(null);
  const [edgarUrl, setEdgarUrl] = useState("");
  const [activeTab, setActiveTab] = useState("edgar");

  // ── Shared polling ────────────────────────────────────────────────────
  const pollStatus = (id) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await getFilingStatus(id);
        if (data.status === "ready") {
          clearInterval(interval);
          setFilingInfo(data);
          setState("ready");
          dispatch(addFiling(data));
        } else if (data.status === "error") {
          clearInterval(interval);
          setError(data.errorMessage || "Extraction failed");
          setState("error");
        }
      } catch {}
    }, 2000);
  };

  const reset = () => {
    setState("idle"); setError(null);
    setFilingId(null); setFilingInfo(null); setProgress(0);
  };

  // ── File upload ───────────────────────────────────────────────────────
  const handleFileUpload = async ({ file }) => {
    reset();
    setState("loading");
    try {
      const { data } = await uploadFiling(file, setProgress);
      setFilingId(data.filingId);
      setState("extracting");
      pollStatus(data.filingId);
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed");
      setState("error");
    }
  };

  // ── EDGAR URL submit ──────────────────────────────────────────────────
  const handleEdgarSubmit = async () => {
    if (!edgarUrl.trim()) return;
    if (!edgarUrl.includes("sec.gov")) {
      setError("URL must be from sec.gov");
      setState("error");
      return;
    }
    reset();
    setState("extracting");
    try {
      const { data } = await submitEdgarUrl(edgarUrl.trim());
      setFilingId(data.filingId);
      pollStatus(data.filingId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch from EDGAR");
      setState("error");
    }
  };

  const currentStep = { idle: 0, loading: 0, extracting: 1, ready: 2, error: 0 }[state];
  const isLoading   = state === "loading" || state === "extracting";

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Title level={3} style={{ color: "#1A3C5E", marginBottom: 4 }}>
        Load SEC Filing
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Paste an SEC EDGAR index URL for the best results — XBRL data is directly structured.
        Or upload a PDF/HTML file.
      </Paragraph>

      {/* Progress Steps */}
      <Card style={{ marginBottom: 20 }}>
        <Steps
          current={currentStep}
          items={[
            { title: "Load",      icon: isLoading && currentStep === 0 ? <LoadingOutlined /> : <InboxOutlined />   },
            { title: "Extract",   icon: state === "extracting"          ? <LoadingOutlined /> : <FileTextOutlined /> },
            { title: "Benchmark", icon: <RocketOutlined /> },
          ]}
        />
      </Card>

      {/* Input area — only show when idle/error */}
      {(state === "idle" || state === "error") && (
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key:   "edgar",
                label: <span><GlobalOutlined /> EDGAR URL (Recommended)</span>,
                children: (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Alert
                      type="info"
                      showIcon
                      message="How to get the URL"
                      description={
                        <ol style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 13 }}>
                          <li>Go to <a href="https://www.sec.gov/cgi-bin/browse-edgar" target="_blank" rel="noreferrer">sec.gov/cgi-bin/browse-edgar</a></li>
                          <li>Search by company name or CIK number</li>
                          <li>Select filing type (10-K, 10-Q) → click the filing date</li>
                          <li>Copy the URL of the <strong>-index.htm</strong> page and paste below</li>
                        </ol>
                      }
                    />

                    <div>
                      <Text strong>EDGAR Filing Index URL</Text>
                      <Input
                        size="large"
                        prefix={<LinkOutlined style={{ color: "#bbb" }} />}
                        placeholder="https://www.sec.gov/ix?doc=/Archives/edgar/data/... or .../0000...-index.htm"
                        value={edgarUrl}
                        onChange={(e) => setEdgarUrl(e.target.value)}
                        onPressEnter={handleEdgarSubmit}
                        style={{ marginTop: 6 }}
                      />
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      block
                      onClick={handleEdgarSubmit}
                      disabled={!edgarUrl.trim()}
                      style={{ background: "#1A3C5E" }}
                    >
                      Fetch &amp; Parse Filing →
                    </Button>

                    <Divider plain style={{ margin: "4px 0" }}>Quick examples</Divider>
                    <Space wrap>
                      {EDGAR_EXAMPLES.map((ex) => (
                        <Tag
                          key={ex.label}
                          color="blue"
                          icon={<FileTextOutlined />}
                          style={{ cursor: "pointer" }}
                          onClick={() => setEdgarUrl(ex.url)}
                        >
                          {ex.label}
                        </Tag>
                      ))}
                    </Space>
                  </Space>
                ),
              },
              {
                key:   "upload",
                label: <span><InboxOutlined /> File Upload</span>,
                children: (
                  <Dragger
                    accept=".pdf,.htm,.html,.txt"
                    beforeUpload={() => false}
                    onChange={({ file }) => { if (file.status !== "removed") handleFileUpload({ file }); }}
                    showUploadList={false}
                    style={{ padding: "20px 0" }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined style={{ fontSize: 48, color: "#2E86AB" }} />
                    </p>
                    <p className="ant-upload-text">Drop your SEC filing here, or click to browse</p>
                    <p className="ant-upload-hint">
                      Supports PDF, HTML, TXT up to 20MB · 10-K · 10-Q · 8-K
                    </p>
                  </Dragger>
                ),
              },
            ]}
          />

          {error && <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />}
        </Card>
      )}

      {/* Loading / uploading */}
      {state === "loading" && (
        <Card>
          <Text>Uploading file…</Text>
          <Progress percent={progress} status="active" style={{ marginTop: 12 }} />
        </Card>
      )}

      {/* Extracting */}
      {state === "extracting" && (
        <Card>
          <Space>
            <LoadingOutlined style={{ color: "#2E86AB", fontSize: 20 }} />
            <div>
              <Text strong>Fetching &amp; extracting filing…</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                {activeTab === "edgar"
                  ? "Downloading from SEC EDGAR and parsing XBRL data. This takes 5–15 seconds."
                  : "Extracting text from file. This takes a few seconds."}
              </Text>
            </div>
          </Space>
        </Card>
      )}

      {/* Ready */}
      {state === "ready" && filingInfo && (
        <Card>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size={4}>
                <Space>
                  <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 20 }} />
                  <Text strong style={{ fontSize: 16 }}>Filing Ready</Text>
                </Space>
                {filingInfo.companyName && (
                  <Text strong style={{ fontSize: 15 }}>{filingInfo.companyName}</Text>
                )}
                <Space wrap>
                  {filingInfo.filingType && <Tag color="blue">{filingInfo.filingType}</Tag>}
                  {filingInfo.ticker     && <Tag color="purple">NYSE/NASDAQ: {filingInfo.ticker}</Tag>}
                  <Tag color={filingInfo.source === "edgar-url" ? "green" : "orange"}>
                    {filingInfo.source === "edgar-url" ? "XBRL / EDGAR" : "File Upload"}
                  </Tag>
                </Space>
                {filingInfo.sourceUrl && (
                  <Text type="secondary" style={{ fontSize: 12 }} copyable>
                    {filingInfo.sourceUrl}
                  </Text>
                )}
              </Space>
            </Col>
          </Row>

          <Divider />

          <Space>
            <Button
              type="primary"
              size="large"
              icon={<RocketOutlined />}
              onClick={() => navigate(`/benchmark/${filingId}`)}
              style={{ background: "#1A3C5E" }}
            >
              Run AI Benchmark →
            </Button>
            <Button onClick={reset}>Load Another</Button>
          </Space>
        </Card>
      )}
    </div>
  );
}